import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { ChartCardSkeleton } from './ChartCardSkeleton';
import { MetricCardSkeleton } from './MetricCardSkeleton';

interface PageContentSkeletonProps {
  metricCount?: number;
  panelCount?: number;
  showToolbar?: boolean;
}

export const PageContentSkeleton = ({
  metricCount = 4,
  panelCount = 2,
  showToolbar = true,
}: PageContentSkeletonProps) => {
  const theme = useTheme();
  const isTabletOrWider = useMediaQuery(theme.breakpoints.up('sm'));
  const isDesktopOrWider = useMediaQuery(theme.breakpoints.up('lg'));
  const metricColumns = isDesktopOrWider ? 4 : isTabletOrWider ? 2 : 1;
  const panelColumns = isDesktopOrWider ? 2 : 1;

  return (
    <Box
    aria-busy="true"
    aria-label="Loading page content"
    role="status"
    style={{ width: '100%' }}
    sx={{ boxSizing: 'border-box', minWidth: 0, p: { xs: 2, sm: 3 }, width: '100%' }}
    >
      <Box aria-hidden="true">
        <Skeleton height={40} variant="text" width="min(100%, 280px)" />
        {showToolbar && (
          <Skeleton
            data-testid="page-skeleton-toolbar"
            height={40}
            sx={{ borderRadius: 1, mt: 2, width: 'min(100%, 420px)' }}
            variant="rounded"
          />
        )}
      </Box>

      <Box
        data-testid="page-skeleton-grid"
        style={{
          gridTemplateColumns: `repeat(${metricColumns}, minmax(0, 1fr))`,
          width: '100%',
        }}
        sx={{
          display: 'grid',
          gap: { xs: 2, sm: 2.5, lg: 3 },
          minWidth: 0,
          mt: 3,
        }}
      >
        {Array.from({ length: metricCount }, (_, index) => (
          <MetricCardSkeleton key={index} />
        ))}
      </Box>

      <Box
        data-testid="page-skeleton-panel-grid"
        style={{
          gridTemplateColumns: `repeat(${panelColumns}, minmax(0, 1fr))`,
          width: '100%',
        }}
        sx={{
          display: 'grid',
          gap: { xs: 2, sm: 2.5, lg: 3 },
          minWidth: 0,
          mt: 3,
        }}
      >
        {Array.from({ length: panelCount }, (_, index) => (
          <ChartCardSkeleton key={index} />
        ))}
      </Box>
    </Box>
  );
};

export default PageContentSkeleton;
